import { Link } from "react-router-dom";
import { Shield } from "lucide-react";
import { BTN, BRAND } from "@/lib/design";

export default function Privacy() {
    return (
        <div className="bg-[#F7F5F0]" data-testid="privacy-page">
            <section className="pt-16 md:pt-24 pb-12">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="h-11 w-11 rounded-full bg-[#E8EFE8] grid place-items-center text-[#0E2A47]">
                            <Shield className="h-5 w-5" />
                        </span>
                        <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                            Privacy & Confidentiality
                        </p>
                    </div>
                    <h1 className="font-[Outfit] text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
                        Privacy Policy
                    </h1>
                    <p className="mt-4 text-[14px] text-[#6F6B63]">
                        Last updated: February 2026
                    </p>
                </div>
            </section>

            <section className="pb-24">
                <article className="max-w-3xl mx-auto px-6 prose prose-neutral prose-headings:font-[Outfit] prose-headings:tracking-tight prose-p:text-[15.5px] prose-p:leading-relaxed prose-p:text-[#1A2230]/85 prose-li:text-[15.5px] prose-li:text-[#1A2230]/85">
                    <h2 className="font-[Outfit] text-2xl font-semibold mt-10 mb-3">
                        Our commitment
                    </h2>
                    <p>
                        {BRAND.name} is committed to protecting the privacy
                        and dignity of the young men in our program, their
                        families, professional partners, and visitors to this
                        website. This notice explains what information we
                        collect, how we use it, and your rights.
                    </p>

                    <h2 className="font-[Outfit] text-2xl font-semibold mt-10 mb-3">
                        Information we collect
                    </h2>
                    <ul className="list-disc pl-6 space-y-1.5">
                        <li>Contact details you submit through our forms (name, email, phone, organization).</li>
                        <li>Referral information voluntarily provided about a youth (age, concerns, urgency).</li>
                        <li>Messages exchanged with our website chat assistant for service improvement.</li>
                        <li>Basic, anonymous analytics about how the website is used.</li>
                    </ul>

                    <h2 className="font-[Outfit] text-2xl font-semibold mt-10 mb-3">
                        How we use information
                    </h2>
                    <p>
                        We use the information you provide solely to respond
                        to your inquiry, coordinate referrals or admissions,
                        improve our services, and comply with legal
                        obligations. We do not sell personal information.
                    </p>

                    <h2 className="font-[Outfit] text-2xl font-semibold mt-10 mb-3">
                        Confidentiality of youth records
                    </h2>
                    <p>
                        Records related to youth in our program are
                        maintained in accordance with the Texas Family Code
                        and applicable state and federal confidentiality
                        laws. Records are released only to authorized parties
                        — including parents, legal guardians, caseworkers and
                        courts — under valid consent or legal order.
                    </p>

                    <h2 className="font-[Outfit] text-2xl font-semibold mt-10 mb-3">
                        Please don't share sensitive details through forms
                    </h2>
                    <p>
                        Our web forms and chat assistant are not secure
                        channels for sensitive personal information. Please
                        avoid sharing a youth's Social Security number or
                        sensitive medical details until we connect through a
                        secure method.
                    </p>

                    <h2 className="font-[Outfit] text-2xl font-semibold mt-10 mb-3">
                        Cookies & analytics
                    </h2>
                    <p>
                        We use minimal cookies and privacy-respecting
                        analytics to understand site usage and improve
                        performance. You can disable cookies through your
                        browser settings.
                    </p>

                    <h2 className="font-[Outfit] text-2xl font-semibold mt-10 mb-3">
                        Your rights
                    </h2>
                    <p>
                        You may request access to, correction of, or deletion
                        of information you have submitted by contacting us at{" "}
                        <a className="text-[#0E2A47] underline" href={`mailto:${BRAND.email}`}>
                            {BRAND.email}
                        </a>.
                    </p>

                    <h2 className="font-[Outfit] text-2xl font-semibold mt-10 mb-3">
                        Contact
                    </h2>
                    <p>
                        {BRAND.name} · {BRAND.location} · {BRAND.phone} ·{" "}
                        <a className="text-[#0E2A47] underline" href={`mailto:${BRAND.email}`}>
                            {BRAND.email}
                        </a>
                        <br />
                        Federal Tax ID (EIN): {BRAND.ein}
                    </p>

                    <div className="mt-10 not-prose">
                        <Link to="/contact" className={BTN.outline} data-testid="privacy-contact-link">
                            Have a question? Contact us
                        </Link>
                    </div>
                </article>
            </section>
        </div>
    );
}
